import "./UnitTransactionsPanel.css";

import { ReactElement } from "react";

import { EllipseGroupBlueIcon } from "../../assets";
import { ProjectIssuanceConstants } from "../../constants";
import { ProjectIssuanceSpecificSchema } from "../../models";
import { GetProjectIssuancesResponse } from "../../service/query";
import {
  Button,
  Column,
  CursorDataGrid,
  Divider,
  FlyoutPanel,
  MultiSelect,
  ProfileMenu,
  ProfileMenuItem,
  Row,
} from "../../widget";
import {
  AddIssuanceTransactionPanel,
  EditIssuanceTransactionPanel,
  IssuanceSummary,
  IssuanceSummaryProps,
  ProgressIssuanceTransactionPanel,
} from "./components";
import { useUnitTransactionsPanel } from "./useUnitTransactionsPanel";

export interface UnitTransactionsPanelFilterObjects {
  projectUuid?: string;
  /*
    Scopes the panel to one company's transactions, matching it on either side of the deal - the CRM company
    record opens the panel this way, from a project it is linked to.
  */
  companyUuid?: string;
  vintage?: number;
  projectTypeDisplayName?: string;
  quantityType?: string;
  habitatGroup?: string;
  habitatCondition?: string;
  distinctiveness?: string;
  // null filters for issuances with no value for the dimension (the "No LPA" aggregate bucket);
  // undefined means the panel is not scoped by that dimension at all.
  lpa?: string | null;
  nca?: string | null;
  lnrs?: string | null;
  /*
    Nutrient Neutrality's "By designated site". Follows the same null/undefined convention as the three
    above: the "No designated site" bucket drills down as null, not as a site literally named that.
  */
  designatedSite?: string | null;
}

/*
  The inventory figures for the project the panel has been filtered to, so a filtered usage can still show the
  summary block without a single issuance to read them from. Only supplied by the board views that group by
  project - grouping by vintage, type, unit, habitat group or condition spans projects, so those views leave it
  out and no summary is rendered.
*/
export interface UnitTransactionsPanelProjectSummary {
  projectName: string;
  availableUnits: number;
  reservedUnits: number;
  soldUnits: number;
  quotedUnits: number;
}

// Provide issuance OR filter
interface UnitTransactionsTaskPanelProps {
  issuance?: GetProjectIssuancesResponse["results"][0];
  // Decides which standard-specific fields the summary shows; absent for non-issuance (filtered) usages.
  issuanceSchema?: ProjectIssuanceSpecificSchema;
  filter?: UnitTransactionsPanelFilterObjects;
  projectSummary?: UnitTransactionsPanelProjectSummary;
  /*
    Whether the viewer may change transactions (MANAGE_PROJECT_ISSUANCES). False hides every write
    affordance — Add transaction, and the per-row Edit and Progress icons — leaving the transactions table
    readable. A quote-expiry reminder reaches recipients who cannot manage issuances (Auditors), so the
    panel has to open for them without offering writes the API would reject. Defaults to true, since every
    other caller reaches the panel from a control already gated on that permission.
  */
  canEdit?: boolean;
  show: boolean;
  onClose: () => void;
  handleRefresh: () => void;
}

export const UnitTransactionsPanel = ({
  issuance,
  issuanceSchema,
  filter,
  projectSummary,
  canEdit = true,
  show,
  onClose,
  handleRefresh,
}: UnitTransactionsTaskPanelProps): JSX.Element => {
  const {
    actionsMenuOptionsList,
    issuanceTransaction,
    handleCloseIssuanceTransactionPanel,
    setShowActionsMenu,
    setShowAddIssuanceTransactionPanel,
    setShowEditColumnsPanel,
    showEditIssuanceTransactionPanel,
    showAddIssuanceTransactionPanel,
    showActionsMenu,
    showEditColumnsPanel,
    filterExportColumns,
    filterExportRows,
    columns,
    exportTable,
    exportFileName,
    defaultEditColumns,
    inventoryTransactionsEditColumnsLocalStorageItemName,
    defaultSortingCriteria,
    dataIsLoading,
    onChange,
    refreshIssuanceTransactions,
    handleRefreshIssuancesAndIssuanceTransactions,
    tagsFilterSelectData,
    selectedTagKeys,
    onTagsFilter,
    projectName,
    portalVariant,
    showProgressIssuanceTransactionPanel,
  } = useUnitTransactionsPanel({
    filter,
    issuance,
    canEdit,
    handleRefresh,
  });

  /*
    The summary describes one issuance where the caller has one, and the filtered project's aggregated
    inventory otherwise. Total units is derived from the parts because the aggregate has no total of its own;
    quoted units are a subset of available, so they are not added in.
  */
  const summary: Omit<IssuanceSummaryProps, "variant"> | undefined = issuance
    ? {
        issuanceSchema,
        standardSpecificIssuanceData: issuance.standardSpecificIssuanceData,
        projectName: projectName ?? "",
        status: issuance.status,
        issuanceDate: issuance.issuanceDate,
        areaHaRemaining: issuance.areaHaRemaining,
        unitQuantityTotal: issuance.unitQuantityTotal,
        unitQuantityAvailable: issuance.unitQuantityAvailable,
        unitQuantityReserved: issuance.unitQuantityReserved,
        unitQuantitySold: issuance.unitQuantitySold,
        quotedUnits: issuance.quotedUnits,
      }
    : projectSummary && {
        projectName: projectSummary.projectName,
        unitQuantityTotal: projectSummary.availableUnits + projectSummary.reservedUnits + projectSummary.soldUnits,
        unitQuantityAvailable: projectSummary.availableUnits,
        unitQuantityReserved: projectSummary.reservedUnits,
        unitQuantitySold: projectSummary.soldUnits,
        quotedUnits: projectSummary.quotedUnits,
      };

  /*
    Passed to the grid's header slot so the controls share the toolbar row in normal flow, and stay
    on screen in the empty state, rather than being offset onto it.
  */
  const filters = (
    <div className="Filters">
      <h3>Filter by</h3>
      <MultiSelect
        className="TagsFilter"
        placeholder="Tags"
        data={tagsFilterSelectData}
        values={selectedTagKeys}
        onChange={(keys) => onTagsFilter(keys)}
        disabled={dataIsLoading}
      />
      {selectedTagKeys.length > 0 && (
        <Button
          text="Clear tags filter"
          variant="reversedPrimary"
          textUnderline
          textBold
          hasNoPadding
          onClick={() => onTagsFilter([])}
          disabled={dataIsLoading}
        />
      )}
    </div>
  );

  const content = (): ReactElement => (
    <div className="UnitTransactionsPanelContent">
      <div className="UpcomingMilestonesTaskTable">
        <Row spacingV="ll">
          <Column span={12}>
            <p className="body1">
              If a new transaction completes or cancels a previous reservation or sale, please settle it against the
              existing transaction in the table to keep the inventory accurate.
            </p>
          </Column>
        </Row>
        <Row spacingV="m">
          <Column span={12}>
            <Divider type="horizontal" />
          </Column>
        </Row>
        {/*
          The trailing divider belongs to the summary - without one there is nothing to separate, and the two
          rules would otherwise stack on an empty gap.
        */}
        {summary && (
          <>
            <IssuanceSummary
              issuanceSchema={summary.issuanceSchema}
              standardSpecificIssuanceData={summary.standardSpecificIssuanceData}
              projectName={summary.projectName}
              status={summary.status}
              issuanceDate={summary.issuanceDate}
              areaHaRemaining={summary.areaHaRemaining}
              unitQuantityTotal={summary.unitQuantityTotal}
              unitQuantityAvailable={summary.unitQuantityAvailable}
              unitQuantityReserved={summary.unitQuantityReserved}
              unitQuantitySold={summary.unitQuantitySold}
              quotedUnits={summary.quotedUnits}
              variant={portalVariant}
            />
            <Row>
              <Column span={12}>
                <Divider type="horizontal" />
              </Column>
            </Row>
          </>
        )}
        <Row spacingV="ml">
          <Column span={12} className="DataGridFiltersEnd">
            <CursorDataGrid
              columns={columns}
              clientSide={false}
              filterable
              header={filters}
              onChange={onChange}
              sortable
              data={[]}
              refresh={refreshIssuanceTransactions}
              onExternalExport={exportTable}
              exportFileName={exportFileName}
              filterExportColumns={filterExportColumns}
              filterExportRows={filterExportRows}
              showEditColumnsPanel={showEditColumnsPanel}
              closeEditColumnsPanel={() => setShowEditColumnsPanel(false)}
              editColumnsText="Select the columns to display in the table. Drag headings to rearrange their left-to-right order."
              defaultEditColumns={defaultEditColumns}
              editColumnsLocalStorageItemName={inventoryTransactionsEditColumnsLocalStorageItemName}
              dataIsLoading={dataIsLoading}
              defaultSortingCriteria={defaultSortingCriteria}
              noDataMessage="No issuance transactions to display"
            />
          </Column>
        </Row>
        <Row>
          <Column span={12}>
            <Button text="Close" onClick={onClose} />
          </Column>
        </Row>
      </div>
    </div>
  );

  const buttons = (): ReactElement => (
    <div className="UnitTransactionsPanelButtons">
      {canEdit && issuance && issuance.status !== ProjectIssuanceConstants.ISSUANCE_STATUS_INACTIVE && (
        <Button text="Add transaction" onClick={() => setShowAddIssuanceTransactionPanel(true)} size="medium" />
      )}
      <div>
        <Button
          text="Quick actions⠀"
          icon={<EllipseGroupBlueIcon />}
          variant="tertiary"
          onClick={() => setShowActionsMenu(true)}
          size="smallLarge"
        />
        <ProfileMenu show={showActionsMenu} onClose={() => setShowActionsMenu(false)} position={{ top: "0" }}>
          {actionsMenuOptionsList.map((el) => (
            <ProfileMenuItem
              key={el.id}
              profileMenuItem={el}
              onClick={() => {
                if (el.action) {
                  el.action();
                }
                setShowActionsMenu(false);
              }}
            />
          ))}
        </ProfileMenu>
      </div>
    </div>
  );

  return (
    <FlyoutPanel
      show={show}
      onClose={onClose}
      size="Large"
      header="Unit transactions"
      contentChildren={
        <>
          {content()}
          <AddIssuanceTransactionPanel
            issuance={issuance}
            variant={portalVariant}
            show={showAddIssuanceTransactionPanel}
            onClose={handleCloseIssuanceTransactionPanel}
            handleRefresh={handleRefreshIssuancesAndIssuanceTransactions}
          />
          {issuanceTransaction && (
            <EditIssuanceTransactionPanel
              issuanceTransaction={issuanceTransaction}
              variant={portalVariant}
              show={showEditIssuanceTransactionPanel}
              onClose={handleCloseIssuanceTransactionPanel}
              handleRefresh={handleRefreshIssuancesAndIssuanceTransactions}
            />
          )}
          {issuanceTransaction && showProgressIssuanceTransactionPanel && (
            <ProgressIssuanceTransactionPanel
              issuanceTransaction={issuanceTransaction}
              issuance={issuance}
              isBiodiversity={
                issuanceTransaction.unitMeasurement === ProjectIssuanceConstants.ISSUANCE_UNIT_MEASUREMENT_BIODIVERSITY
              }
              variant={portalVariant}
              show={showProgressIssuanceTransactionPanel}
              onClose={handleCloseIssuanceTransactionPanel}
              handleRefresh={handleRefreshIssuancesAndIssuanceTransactions}
            />
          )}
        </>
      }
      buttonsChildren={buttons()}
    />
  );
};
